import React, {
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import axios from "axios";

import PhoneInput from "react-phone-input-2";

import "react-phone-input-2/lib/style.css";

import "./PaymentPageVisionIgnite.css";


// =====================================================
// SERVICE CONFIG
// =====================================================

const SERVICE_NAME =
  "Forge Your Peak - Vision Ignite";

// Normal price in rupees
const NORMAL_PRICE = 8999;

// Normal price in paise
const NORMAL_AMOUNT = 899900;

// =====================================================
// COMPONENT
// =====================================================

const PaymentPageVisionIgnite = () => {
  const navigate = useNavigate();

  // ===================================================
  // URL OFFER CODE
  //
  // Normal:
  // /vision-ignite-payment
  //
  // Offer:
  // /vision-ignite-payment?offer=vision_ignite7999
  // ===================================================

  const searchParams =
    new URLSearchParams(
      window.location.search
    );

  const OFFER_CODE =
    searchParams
      .get("offer")
      ?.trim()
      .toLowerCase() || null;

  // ===================================================
  // FORM
  // ===================================================

  const [
    formData,
    setFormData,
  ] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // ===================================================
  // PAYMENT LOADING
  // ===================================================

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);

  // ===================================================
  // OFFER DATA
  // ===================================================

  const [
    offerData,
    setOfferData,
  ] = useState(null);

  // ===================================================
  // OFFER LOADING
  // ===================================================

  const [
    isOfferLoading,
    setIsOfferLoading,
  ] = useState(
    Boolean(OFFER_CODE)
  );

  // ===================================================
  // OFFER ERROR
  // ===================================================

  const [
    offerError,
    setOfferError,
  ] = useState("");

  // ===================================================
  // SCROLL TOP
  // ===================================================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ===================================================
  // HIDE NAVBAR / FOOTER
  // ===================================================

  useEffect(() => {
    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector("footer");

    if (navbar) {
      navbar.style.display = "none";
    }

    if (footer) {
      footer.style.display = "none";
    }

    window.scrollTo(0, 0);

    return () => {
      if (navbar) {
        navbar.style.display = "";
      }

      if (footer) {
        footer.style.display = "";
      }
    };
  }, []);

  // ===================================================
  // FETCH ACTIVE OFFER
  // ===================================================

  useEffect(() => {
    const fetchOffer = async () => {

      // -----------------------------------------------
      // No offer code
      // -----------------------------------------------

      if (!OFFER_CODE) {
        setOfferData(null);
        setOfferError("");
        setIsOfferLoading(false);

        return;
      }

      try {
        setIsOfferLoading(true);
        setOfferError("");

        // ---------------------------------------------
        // Public offer API
        // ---------------------------------------------

        const response =
          await axios.get(
            `/api/offers/${encodeURIComponent(
              OFFER_CODE
            )}`
          );

        const data =
          response.data;

        console.log(
          "Vision Ignite Offer:",
          data
        );

        // ---------------------------------------------
        // Validate response
        // ---------------------------------------------

        if (
          !data ||
          !data.success ||
          !data.offer
        ) {
          throw new Error(
            data?.message ||
              "Offer is unavailable."
          );
        }

        // ---------------------------------------------
        // Validate service
        // ---------------------------------------------

        if (
          data.offer.service !==
          SERVICE_NAME
        ) {
          throw new Error(
            "This offer is not valid for Vision Ignite."
          );
        }

        // ---------------------------------------------
        // Validate active
        // ---------------------------------------------

        if (
          data.offer.isActive !==
          true
        ) {
          throw new Error(
            "This offer is currently unavailable."
          );
        }

        // ---------------------------------------------
        // Amounts
        // ---------------------------------------------

        const originalAmount =
          Number(
            data.offer.originalAmount
          );

        const offerAmount =
          Number(
            data.offer.offerAmount
          );

        // ---------------------------------------------
        // Validate original amount
        // ---------------------------------------------

        if (
          !Number.isInteger(
            originalAmount
          ) ||
          originalAmount <= 0
        ) {
          throw new Error(
            "Invalid original offer amount."
          );
        }

        // ---------------------------------------------
        // Validate offer amount
        // ---------------------------------------------

        if (
          !Number.isInteger(
            offerAmount
          ) ||
          offerAmount <= 0
        ) {
          throw new Error(
            "Invalid offer amount."
          );
        }

        // ---------------------------------------------
        // Offer must be cheaper
        // ---------------------------------------------

        if (
          offerAmount >=
          originalAmount
        ) {
          throw new Error(
            "Invalid offer configuration."
          );
        }

        // ---------------------------------------------
        // Save offer
        // ---------------------------------------------

        setOfferData({
          ...data.offer,

          originalAmount,

          offerAmount,
        });

      } catch (error) {
        console.error(
          "❌ Vision Ignite offer error:",
          error
        );

        setOfferData(null);

        setOfferError(
          error.response?.data?.message ||
            error.message ||
            "This offer is currently unavailable."
        );
      } finally {
        setIsOfferLoading(false);
      }
    };

    fetchOffer();
  }, [OFFER_CODE]);

  // ===================================================
  // OFFER ACTIVE
  // ===================================================

  const isOfferActive =
    Boolean(
      OFFER_CODE &&
        offerData &&
        offerData.isActive === true
    );

  // ===================================================
  // DISPLAY PRICE
  // ===================================================

  const displayPrice =
    isOfferActive
      ? offerData.offerAmount / 100
      : NORMAL_PRICE;

  // ===================================================
  // ORIGINAL DISPLAY PRICE
  // ===================================================

  const displayOriginalPrice =
    isOfferActive
      ? offerData.originalAmount / 100
      : NORMAL_PRICE;

  // ===================================================
  // FORM CHANGE
  // ===================================================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ===================================================
  // PHONE CHANGE
  // ===================================================

  const handlePhoneChange = (
    phone
  ) => {
    setFormData((prev) => ({
      ...prev,
      phone,
    }));
  };

  // ===================================================
  // PAYMENT
  // ===================================================

  const handlePayment =
    async () => {
      try {

        // =============================================
        // OFFER STILL LOADING
        // =============================================

        if (isOfferLoading) {
          alert(
            "Checking offer availability. Please wait."
          );

          return;
        }

        // =============================================
        // OFFER INVALID
        // =============================================

        if (
          OFFER_CODE &&
          !isOfferActive
        ) {
          alert(
            offerError ||
              "This offer is currently unavailable."
          );

          return;
        }

        // =============================================
        // NAME
        // =============================================

        if (
          !formData.name.trim()
        ) {
          alert(
            "Please enter your full name."
          );

          return;
        }

        // =============================================
        // EMAIL
        // =============================================

        if (
          !formData.email.trim()
        ) {
          alert(
            "Please enter your email address."
          );

          return;
        }

        // =============================================
        // PHONE
        // =============================================

        if (
          !formData.phone.trim()
        ) {
          alert(
            "Please enter your phone number."
          );

          return;
        }

        // =============================================
        // RAZORPAY CHECK
        // =============================================

        if (!window.Razorpay) {
          alert(
            "Razorpay checkout is not loaded."
          );

          return;
        }

        setIsLoading(true);

        // =============================================
        // FINAL OFFER
        // =============================================

        const finalOffer =
          isOfferActive
            ? OFFER_CODE
            : null;

        // =============================================
        // CREATE ORDER
        // =============================================

        const response =
          await axios.post(
            "/api/razorpay/create-order",
            {
              name:
                formData.name.trim(),

              email:
                formData.email
                  .trim()
                  .toLowerCase(),

              phone:
                formData.phone,

              service:
                SERVICE_NAME,

              offer:
                finalOffer,
            }
          );

        const data =
          response.data;

        console.log(
          "Razorpay Order Response:",
          data
        );

        // =============================================
        // BACKEND RESPONSE
        // =============================================

        if (!data.success) {
          throw new Error(
            data.message ||
              "Unable to create payment order."
          );
        }

        if (
          !data.order ||
          !data.order.id
        ) {
          throw new Error(
            "Invalid Razorpay order response."
          );
        }

        // =============================================
        // BACKEND IS SOURCE OF TRUTH
        // =============================================

        const backendAmount =
          Number(
            data.order.amount
          );

        if (
          !Number.isInteger(
            backendAmount
          ) ||
          backendAmount <= 0
        ) {
          throw new Error(
            "Invalid payment amount received from server."
          );
        }

        // =============================================
        // RAZORPAY OPTIONS
        // =============================================

        const options = {
          key:
            data.keyId,

          amount:
            backendAmount,

          currency:
            data.order.currency ||
            "INR",

          name:
            "Forge Your Peak",

          description:
            SERVICE_NAME,

          order_id:
            data.order.id,

          prefill: {
            name:
              formData.name.trim(),

            email:
              formData.email
                .trim()
                .toLowerCase(),

            contact:
              formData.phone,
          },

          notes: {
            service:
              SERVICE_NAME,

            offer:
              finalOffer ||
              "normal",
          },

          theme: {
            color:
              "#20264F",
          },

          // =========================================
          // PAYMENT SUCCESS
          // =========================================

          handler:
            async function (
              paymentResponse
            ) {
              try {

                console.log(
                  "✅ Razorpay Payment Response:",
                  paymentResponse
                );

                // ===================================
                // VERIFY PAYMENT
                // ===================================

                const verifyResponse =
                  await axios.post(
                    "/api/razorpay/verify-payment",
                    {
                      razorpay_order_id:
                        paymentResponse
                          .razorpay_order_id,

                      razorpay_payment_id:
                        paymentResponse
                          .razorpay_payment_id,

                      razorpay_signature:
                        paymentResponse
                          .razorpay_signature,
                    }
                  );

                const verifyData =
                  verifyResponse.data;

                console.log(
                  "Payment Verification Response:",
                  verifyData
                );

                // ===================================
                // VERIFICATION FAILED
                // ===================================

                if (
                  !verifyData.success
                ) {
                  throw new Error(
                    verifyData.message ||
                      "Payment verification failed."
                  );
                }

                console.log(
                  "✅ Payment verified successfully."
                );

                // ===================================
                // META PURCHASE
                // ===================================

                if (window.fbq) {
                  window.fbq(
                    "trackSingle",

                    "2184603352474386",

                    "Purchase",

                    {
                      content_category:
                        "Purchase",

                      content_name:
                        SERVICE_NAME,

                      value:
                        backendAmount / 100,

                      currency:
                        "INR",

                      contents: [
                        {
                          id:
                            finalOffer ||
                            "vision-ignite",

                          quantity: 1,
                        },
                      ],

                      content_type:
                        "product",
                    }
                  );

                  console.log(
                    "✅ Meta Purchase event tracked."
                  );
                }

                // ===================================
                // STOP LOADING
                // ===================================

                setIsLoading(false);

                // ===================================
                // SUCCESS MESSAGE
                // ===================================

                alert(
                  "Payment successful! Your access has been activated."
                );

                // ===================================
                // DASHBOARD
                // ===================================

                navigate(
                  "/dashboard-login",
                  {
                    replace: true,
                  }
                );

              } catch (error) {

                console.error(
                  "❌ Payment verification error:",
                  error
                );

                setIsLoading(false);

                alert(
                  error.response?.data
                    ?.message ||
                    error.message ||
                    "Payment verification failed. Please contact support."
                );
              }
            },

          // =========================================
          // CHECKOUT CLOSED
          // =========================================

          modal: {
            ondismiss:
              function () {
                console.log(
                  "Razorpay Checkout closed."
                );

                setIsLoading(false);
              },
          },
        };

        // =============================================
        // RAZORPAY INSTANCE
        // =============================================

        const razorpay =
          new window.Razorpay(
            options
          );

        // =============================================
        // PAYMENT FAILED
        // =============================================

        razorpay.on(
          "payment.failed",
          function (response) {

            console.error(
              "❌ Razorpay payment failed:",
              response.error
            );

            setIsLoading(false);

            alert(
              response.error?.description ||
                "Payment failed. Please try again."
            );
          }
        );

        // =============================================
        // OPEN CHECKOUT
        // =============================================

        razorpay.open();

      } catch (error) {

        console.error(
          "❌ Payment initialization error:",
          error
        );

        setIsLoading(false);

        alert(
          error.response?.data?.message ||
            error.message ||
            "Something went wrong while starting the payment."
        );
      }
    };

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <div className="vision-payment-page">

      {/* ==========================================
          LEFT SECTION
      ========================================== */}

      <section className="vision-payment-left">

        <div className="vision-left-inner">

          {/* Logo */}

          <div className="vision-company-logo">

            <img
              src="/Logo.png"
              alt="Forge Your Peak"
            />

          </div>

          {/* Product Header */}

          <div className="vision-product-header">

            <h1>
              Forge Your Peak – Vision Ignite
            </h1>

            <p>
              By ForgeYourPeak
            </p>

            {/* ======================================
                PRICE
            ====================================== */}

            <div className="vision-product-price">

              {isOfferActive ? (
                <>
                  <span
                    style={{
                      textDecoration:
                        "line-through",

                      color:
                        "#6b7280",

                      fontSize:
                        "22px",

                      marginRight:
                        "12px",
                    }}
                  >
                    ₹
                    {displayOriginalPrice}
                  </span>

                  <span>
                    ₹
                    {displayPrice}
                  </span>
                </>
              ) : (
                <>
                  ₹
                  {NORMAL_PRICE}
                </>
              )}

            </div>

            {/* ======================================
                OFFER LOADING
            ====================================== */}

            {isOfferLoading && (
              <div
                style={{
                  marginTop:
                    "10px",

                  color:
                    "#6b7280",

                  fontSize:
                    "14px",
                }}
              >
                Checking offer...
              </div>
            )}

            {/* ======================================
                ACTIVE OFFER
            ====================================== */}

            {isOfferActive && (
              <div
                style={{
                  display:
                    "inline-block",

                  marginTop:
                    "8px",

                  padding:
                    "6px 12px",

                  borderRadius:
                    "6px",

                  background:
                    "#fff1f2",

                  color:
                    "#d60a17",

                  fontWeight:
                    "700",

                  fontSize:
                    "14px",
                }}
              >
                SPECIAL OFFER
              </div>
            )}

            {/* ======================================
                OFFER ERROR
            ====================================== */}

            {OFFER_CODE &&
              !isOfferLoading &&
              !isOfferActive && (
                <div
                  style={{
                    marginTop:
                      "10px",

                    padding:
                      "10px 12px",

                    borderRadius:
                      "6px",

                    background:
                      "#fff1f2",

                    color:
                      "#b91c1c",

                    fontWeight:
                      "600",

                    fontSize:
                      "14px",
                  }}
                >
                  {offerError ||
                    "This offer is currently unavailable."}
                </div>
              )}

          </div>

          {/* Product Image */}

          <div className="vision-product-image-wrapper">

            <img
              src="/Ignite-Product.png"
              alt="Forge Your Peak Vision Ignite"
              className="vision-product-image"
            />

          </div>

          {/* Description */}

          <div className="vision-product-description">

            <p>
              <strong>
                Vision Ignite
              </strong>{" "}
              is a 90-day self-guided system
              designed to help you take the
              first real step toward reducing
              your dependence on glasses—by
              retraining how your eyes and brain
              work together.
            </p>

            <p>
              This is not a course you watch.
              It’s a system you follow.
            </p>

            <p>
              Every day, you’ll be guided through
              a simple 10–12 minute protocol that
              helps relax visual strain, activate
              eye movement, and improve your
              ability to shift and hold focus.
            </p>

            <p>
              No guesswork, no overwhelm—just a
              structured routine designed to build
              consistency and awareness.
            </p>

            <p>
              Most people with vision issues don’t
              have “weak eyes”—they have eyes that
              are constantly under strain. Vision
              Ignite helps you reverse that pattern
              by teaching your visual system how to
              function in a more relaxed and
              efficient way.
            </p>

            <p>
              As you progress, you’ll start noticing
              subtle but important shifts—less eye
              fatigue, better screen comfort, and
              moments of clearer vision. These are
              the first signs that your system is
              adapting.
            </p>

            <p>
              The program is completely self-paced
              and requires minimal intervention,
              but it is built with progression in
              mind. With built-in tracking, streaks,
              and milestone-based phases, you’ll
              always know where you stand and what
              comes next.
            </p>

            <p>
              By the end of 90 days, you won’t just
              be doing exercises—you’ll understand
              your vision better, build a consistent
              routine, and be ready to move into
              deeper transformation.
            </p>

            <p>
              This is where your journey toward
              vision freedom begins.
            </p>

          </div>

        </div>

      </section>

      {/* ==========================================
          RIGHT SECTION
      ========================================== */}

      <section className="vision-payment-right">

        <div className="vision-payment-right-inner">

          <h2>
            Payment details
          </h2>

          <p className="vision-payment-subtitle">
            Complete your purchase by providing
            your payment details.
          </p>

          {/* ======================================
              BILLING
          ====================================== */}

          <div className="vision-billing-card">

            <h3>
              Billing information
            </h3>

            <div className="vision-billing-fields">

              {/* Name */}

              <input
                type="text"
                name="name"
                placeholder="Full name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
              />

              {/* Email */}

              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />

              {/* Phone */}

              <div className="vision-phone-wrapper">

                <PhoneInput
                  country="in"

                  value={
                    formData.phone
                  }

                  onChange={
                    handlePhoneChange
                  }

                  enableSearch={
                    true
                  }

                  countryCodeEditable={
                    false
                  }

                  placeholder={
                    "Phone number"
                  }

                  inputProps={{
                    name:
                      "phone",

                    required:
                      true,
                  }}

                  containerClass={
                    "vision-phone-container"
                  }

                  inputClass={
                    "vision-phone-input"
                  }

                  buttonClass={
                    "vision-phone-button"
                  }

                  dropdownClass={
                    "vision-phone-dropdown"
                  }

                  disabled={
                    isLoading
                  }
                />

              </div>

            </div>

          </div>

          {/* ======================================
              ORDER SUMMARY
          ====================================== */}

          <div className="vision-order-card">

            <div className="vision-order-top-line"></div>

            <div className="vision-order-content">

              <h3>
                Service
              </h3>

              <div className="vision-service-row">

                <span>
                  {SERVICE_NAME}
                </span>

                <strong>

                  {isOfferActive && (
                    <span
                      style={{
                        textDecoration:
                          "line-through",

                        color:
                          "#6b7280",

                        fontSize:
                          "14px",

                        marginRight:
                          "8px",
                      }}
                    >
                      ₹
                      {displayOriginalPrice}
                    </span>
                  )}

                  ₹
                  {displayPrice}.00

                </strong>

              </div>

              {/* Offer status */}

              {isOfferActive && (
                <div
                  style={{
                    marginTop:
                      "8px",

                    fontSize:
                      "13px",

                    color:
                      "#16a34a",

                    fontWeight:
                      "600",
                  }}
                >
                  Special offer applied
                </div>
              )}

              <div className="vision-divider"></div>

              <div className="vision-total-row">

                <span>
                  Amount to be paid :
                </span>

                <strong>
                  ₹
                  {displayPrice}.00
                </strong>

              </div>

            </div>

          </div>

          {/* ======================================
              PAY BUTTON
          ====================================== */}

          <button
            type="button"
            className="vision-pay-button"
            onClick={
              handlePayment
            }
            disabled={
              isLoading ||
              isOfferLoading ||
              Boolean(
                OFFER_CODE &&
                  !isOfferActive
              )
            }
          >
            {isLoading
              ? "Processing..."
              : isOfferLoading
              ? "Checking offer..."
              : `Proceed to pay ₹${displayPrice}.00`}
          </button>

        </div>

      </section>

    </div>
  );
};

export default PaymentPageVisionIgnite;